import { useState, type ChangeEvent, type FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import Layout from './Layout';
import { Button } from './ui/button';

const GOOGLE_FORM_ACTION =
  'https://docs.google.com/forms/d/e/1FAIpQLSckY_QcN8GY9okZChahgEoiBug5PfJ_8czJAntWeDRuoUO0GQ/formResponse';

type FieldId =
  | 'fullName'
  | 'companyName'
  | 'companyWebsite'
  | 'jobTitle'
  | 'businessEmail'
  | 'phoneNumber'
  | 'teamSize';

type FormState = Record<FieldId, string>;

const FIELDS: {
  id: FieldId;
  label: string;
  type: string;
  entry: string;
  autoComplete?: string;
  inputMode?: 'url' | 'numeric';
  placeholder?: string;
}[] = [
  {
    id: 'fullName',
    label: 'Full Name',
    type: 'text',
    entry: 'entry.1570641430',
    autoComplete: 'name',
  },
  {
    id: 'companyName',
    label: 'Company Name',
    type: 'text',
    entry: 'entry.443969203',
    autoComplete: 'organization',
  },
  {
    id: 'companyWebsite',
    label: 'Company Website',
    type: 'text',
    entry: 'entry.310155146',
    autoComplete: 'url',
    inputMode: 'url',
    placeholder: 'yourcompany.com',
  },
  {
    id: 'jobTitle',
    label: 'Job Title',
    type: 'text',
    entry: 'entry.1259807625',
    autoComplete: 'organization-title',
  },
  {
    id: 'businessEmail',
    label: 'Business Email',
    type: 'email',
    entry: 'entry.438715471',
    autoComplete: 'email',
  },
  {
    id: 'phoneNumber',
    label: 'Phone Number',
    type: 'tel',
    entry: 'entry.347593975',
    autoComplete: 'tel',
  },
  {
    id: 'teamSize',
    label: 'Team Size',
    type: 'number',
    entry: 'entry.743652006',
    inputMode: 'numeric',
    placeholder: 'Number of developers',
  },
];

const INITIAL_STATE: FormState = FIELDS.reduce((acc, field) => {
  acc[field.id] = '';
  return acc;
}, {} as FormState);

export default function CybersecurityAwarenessMonthApplyPage() {
  const [formData, setFormData] = useState<FormState>(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const body = new URLSearchParams();
    for (const field of FIELDS) {
      body.append(field.entry, formData[field.id]);
    }

    try {
      await fetch(GOOGLE_FORM_ACTION, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <Layout>
      <div className="mx-auto flex max-w-3xl flex-col gap-8 py-10">
        <div className="text-center">
          <span className="text-lightBlue text-[15px] font-bold uppercase">
            Cybersecurity Awareness Month Offer
          </span>
          <h1 className="mt-3 text-4xl font-bold">Application Form</h1>
        </div>

        {submitted ? (
          <div className="bg-gray flex flex-col items-center gap-5 rounded-lg p-12 text-center">
            <div className="rounded-full bg-white p-4">
              <CheckCircle2 className="text-lightBlue" size={48} />
            </div>
            <h2 className="text-[28px] font-semibold">Application Submitted Successfully</h2>
            <p className="text-lighterBlack max-w-lg text-lg">
              Thank you for applying. The Simulations Labs team will review your application and
              contact selected applicants to discuss the next steps.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-gray space-y-4 rounded-lg p-6">
              <p className="text-lighterBlack">
                Simulations Labs is offering $1,000 in platform credit at no cost to selected
                organizations looking to strengthen their developers' secure coding skills.
              </p>
              <p className="text-lighterBlack">
                Use the platform to engage your team in secure coding competitions, assess
                developers' skills through realistic vulnerability scenarios, or provide hands-on
                training to help them identify and fix real vulnerabilities.
              </p>
              <p className="text-lighterBlack">
                Submit your application below. Applications will be reviewed by the Simulations Labs
                team, and selected applicants will be contacted to discuss the next steps.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5 rounded-lg border border-gray-200 bg-white p-8"
            >
              {FIELDS.map((field) => (
                <div key={field.id} className="flex flex-col gap-2">
                  <label htmlFor={field.id} className="text-sm font-medium text-gray-700">
                    {field.label} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={field.id}
                    name={field.id}
                    type={field.type}
                    value={formData[field.id]}
                    onChange={handleChange}
                    autoComplete={field.autoComplete}
                    inputMode={field.inputMode}
                    placeholder={field.placeholder}
                    min={field.type === 'number' ? 1 : undefined}
                    required
                    className="focus:border-lightBlue w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-0 focus:outline-none"
                  />
                </div>
              ))}

              <Button type="submit" disabled={submitting} className="mt-2 w-full">
                {submitting ? 'Submitting…' : 'Submit Application'}
              </Button>
            </form>
          </>
        )}
      </div>
    </Layout>
  );
}
